// Nintendo-Inspired Mobile Controller for Taronno's Love
// Positioned in the Left & Right Black Pillarbox Side Borders
// Leaves the central 16:9 gameplay canvas 100% clean and unblocked.

export class TouchControls {
  constructor(scene, mode = 'adventure', callbacks = {}) {
    this.scene = scene;
    this.mode = mode; // 'adventure' or 'combat'
    this.callbacks = callbacks;
    this.container = null;
    this.leftWing = null;
    this.rightWing = null;
    this.isTouchDevice = this.detectTouchDevice();
    this.interactBtn = null;
    this.boundUpdateLayout = this.updateLayout.bind(this);
  }

  detectTouchDevice() {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches
    );
  }

  mount() {
    this.destroy();

    // Do not mount touch controls on desktop mouse/keyboard setups
    if (!this.isTouchDevice) return;

    const app = document.getElementById('app') || document.body;
    if (!app) return;

    this.container = document.createElement('div');
    this.container.id = 'touch-controls-root';
    this.container.className = `touch-controls-root mode-${this.mode}`;

    // 1. Left Border Wing: Movement Joystick
    this.leftWing = document.createElement('div');
    this.leftWing.className = 'touch-border-wing touch-border-left';
    this.buildJoystick(this.leftWing);

    // 2. Right Border Wing: Nintendo Action Buttons
    this.rightWing = document.createElement('div');
    this.rightWing.className = 'touch-border-wing touch-border-right';
    if (this.mode === 'adventure') {
      this.buildAdventureActions(this.rightWing);
    } else if (this.mode === 'combat') {
      this.buildCombatActions(this.rightWing);
    }

    this.container.appendChild(this.leftWing);
    this.container.appendChild(this.rightWing);
    app.appendChild(this.container);

    // Initial layout calculation
    this.updateLayout();

    window.addEventListener('resize', this.boundUpdateLayout);
    window.addEventListener('orientationchange', this.boundUpdateLayout);
    window.addEventListener('mobile-layout-refresh', this.boundUpdateLayout);
  }

  updateLayout() {
    if (!this.container || !this.leftWing || !this.rightWing) return;

    const app = document.getElementById('app') || document.body;
    const gameShell = document.getElementById('game-shell');
    if (!gameShell || !app) return;

    const appRect = app.getBoundingClientRect();
    const shellRect = gameShell.getBoundingClientRect();

    // Calculate actual pixel width of black pillarbox side borders
    const leftBorderWidth = Math.max(0, shellRect.left - appRect.left);
    const rightBorderWidth = Math.max(0, appRect.right - shellRect.right);

    const hasPillarboxes = leftBorderWidth >= 48 && rightBorderWidth >= 48;

    this.container.classList.toggle('in-pillarboxes', hasPillarboxes);
    this.container.classList.toggle('fallback-overlay', !hasPillarboxes);

    if (hasPillarboxes) {
      // Place Left Wing ENTIRELY inside the Left Black Border
      this.leftWing.style.position = 'absolute';
      this.leftWing.style.left = '0px';
      this.leftWing.style.top = '0px';
      this.leftWing.style.bottom = '0px';
      this.leftWing.style.width = `${leftBorderWidth}px`;
      this.leftWing.style.right = 'auto';

      // Place Right Wing ENTIRELY inside the Right Black Border
      this.rightWing.style.position = 'absolute';
      this.rightWing.style.right = '0px';
      this.rightWing.style.top = '0px';
      this.rightWing.style.bottom = '0px';
      this.rightWing.style.width = `${rightBorderWidth}px`;
      this.rightWing.style.left = 'auto';

      // Dynamically scale joystick to fit left border width
      const maxJoySize = Math.max(54, Math.min(74, leftBorderWidth - 10));
      const joyBase = this.leftWing.querySelector('.nintendo-joystick-base');
      const joyZone = this.leftWing.querySelector('.nintendo-joystick-zone');
      if (joyBase) {
        joyBase.style.width = `${maxJoySize}px`;
        joyBase.style.height = `${maxJoySize}px`;
      }
      if (joyZone) {
        joyZone.style.width = `${maxJoySize + 8}px`;
        joyZone.style.height = `${maxJoySize + 8}px`;
      }

      // Dynamically scale buttons to fit right border width
      const maxBtnSize = Math.max(38, Math.min(48, rightBorderWidth - 16));
      const buttons = this.rightWing.querySelectorAll('.nintendo-btn');
      buttons.forEach(btn => {
        btn.style.width = `${maxBtnSize}px`;
        btn.style.height = `${maxBtnSize}px`;
      });
    } else {
      // Fallback for screens without side pillarboxes: position at extreme outer corners
      this.leftWing.style.position = 'absolute';
      this.leftWing.style.left = 'max(8px, env(safe-area-inset-left, 8px))';
      this.leftWing.style.bottom = 'max(8px, env(safe-area-inset-bottom, 8px))';
      this.leftWing.style.top = 'auto';
      this.leftWing.style.width = 'auto';

      this.rightWing.style.position = 'absolute';
      this.rightWing.style.right = 'max(8px, env(safe-area-inset-right, 8px))';
      this.rightWing.style.bottom = 'max(8px, env(safe-area-inset-bottom, 8px))';
      this.rightWing.style.top = 'auto';
      this.rightWing.style.width = 'auto';
    }
  }

  buildJoystick(parent) {
    const joyZone = document.createElement('div');
    joyZone.className = 'nintendo-joystick-zone';
    joyZone.id = 'nintendo-joystick-zone';
    joyZone.setAttribute('aria-label', 'Directional Control');

    const joyBase = document.createElement('div');
    joyBase.className = 'nintendo-joystick-base';

    const pips = document.createElement('div');
    pips.className = 'nintendo-joystick-pips';

    const stick = document.createElement('div');
    stick.className = 'nintendo-joystick-stick';

    joyBase.appendChild(pips);
    joyBase.appendChild(stick);
    joyZone.appendChild(joyBase);
    parent.appendChild(joyZone);

    let pointerId = null;

    const updateStick = (clientX, clientY) => {
      const rect = joyBase.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dynamicMaxRadius = Math.max(16, (rect.width / 2) - 14);

      const dx = clientX - centerX;
      const dy = clientY - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 4) {
        stick.style.transform = 'translate3d(0, 0, 0)';
        if (this.callbacks.onMove) this.callbacks.onMove(0, 0);
        return;
      }

      const clampedDist = Math.min(dist, dynamicMaxRadius);
      const angle = Math.atan2(dy, dx);

      const stickX = clampedDist * Math.cos(angle);
      const stickY = clampedDist * Math.sin(angle);

      stick.style.transform = `translate3d(${stickX}px, ${stickY}px, 0)`;

      // Proportional speed response (-1.0 to 1.0)
      const normX = stickX / dynamicMaxRadius;
      const normY = stickY / dynamicMaxRadius;

      if (this.callbacks.onMove) {
        this.callbacks.onMove(normX, normY);
      }
    };

    const onStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (pointerId !== null) return;
      pointerId = e.pointerId;
      try {
        joyZone.setPointerCapture(e.pointerId);
      } catch (_) {}
      joyBase.classList.add('active');
      updateStick(e.clientX, e.clientY);
    };

    const onMove = (e) => {
      if (e.pointerId !== pointerId) return;
      e.preventDefault();
      e.stopPropagation();
      updateStick(e.clientX, e.clientY);
    };

    const onEnd = (e) => {
      if (e.pointerId !== pointerId) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        joyZone.releasePointerCapture(e.pointerId);
      } catch (_) {}
      pointerId = null;
      joyBase.classList.remove('active');
      stick.style.transform = 'translate3d(0, 0, 0)';
      if (this.callbacks.onMove) this.callbacks.onMove(0, 0);
    };

    joyZone.addEventListener('pointerdown', onStart, { passive: false });
    joyZone.addEventListener('pointermove', onMove, { passive: false });
    joyZone.addEventListener('pointerup', onEnd, { passive: false });
    joyZone.addEventListener('pointercancel', onEnd, { passive: false });
    joyZone.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  buildAdventureActions(parent) {
    const pad = document.createElement('div');
    pad.className = 'nintendo-pad nintendo-adventure-pad';

    // Button A: INTERACT / TALK (Primary action on top / right)
    this.interactBtn = this.createButton(
      'btn-nintendo-a',
      'A',
      'INTERACT',
      'btn-accent-maroon',
      (pressed) => {
        if (pressed && this.callbacks.onInteract) this.callbacks.onInteract();
      }
    );

    // Button B: JUMP (Secondary action below / left)
    const btnJump = this.createButton(
      'btn-nintendo-b',
      'B',
      'JUMP',
      'btn-accent-slate',
      (pressed) => {
        if (pressed && this.callbacks.onJump) this.callbacks.onJump();
      }
    );

    pad.appendChild(this.interactBtn);
    pad.appendChild(btnJump);
    parent.appendChild(pad);
  }

  buildCombatActions(parent) {
    const pad = document.createElement('div');
    pad.className = 'nintendo-pad nintendo-combat-pad';

    // 4-Button Nintendo Diamond Cluster:
    // X (top) = PARRY, Y (left) = DASH, A (right) = SLASH, B (bottom) = CRIT
    const btnParry = this.createButton('btn-nintendo-x', 'X', 'PARRY', 'btn-accent-emerald', (pressed) => {
      if (pressed && this.callbacks.onParry) this.callbacks.onParry();
    });

    const btnDash = this.createButton('btn-nintendo-y', 'Y', 'DASH', 'btn-accent-amethyst', (pressed) => {
      if (pressed && this.callbacks.onDash) this.callbacks.onDash();
    });

    const btnSlash = this.createButton('btn-nintendo-a', 'A', 'SLASH', 'btn-accent-maroon', (pressed) => {
      if (pressed && this.callbacks.onSlash) this.callbacks.onSlash();
    });

    const btnCrit = this.createButton('btn-nintendo-b', 'B', 'CRIT', 'btn-accent-gold', (pressed) => {
      if (pressed && this.callbacks.onCrit) this.callbacks.onCrit();
    });

    pad.appendChild(btnParry);
    pad.appendChild(btnDash);
    pad.appendChild(btnSlash);
    pad.appendChild(btnCrit);
    parent.appendChild(pad);
  }

  createButton(id, letter, labelText, accentClass, onStateChange) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.id = id;
    btn.className = `nintendo-btn ${accentClass}`;
    btn.style.touchAction = 'none';
    btn.setAttribute('aria-label', `${letter} - ${labelText}`);

    btn.innerHTML = `
      <span class="nintendo-btn-face">
        <span class="nintendo-btn-letter">${letter}</span>
        <span class="nintendo-btn-sub">${labelText}</span>
      </span>
    `;

    let isPressed = false;

    const press = (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        if (btn.setPointerCapture && e.pointerId) {
          btn.setPointerCapture(e.pointerId);
        }
      } catch (_) {}

      if (!isPressed) {
        isPressed = true;
        btn.classList.add('active');
        if (navigator.vibrate) {
          try { navigator.vibrate(8); } catch (_) {}
        }
        onStateChange(true);
      }
    };

    const release = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
        try {
          if (btn.releasePointerCapture && e.pointerId) {
            btn.releasePointerCapture(e.pointerId);
          }
        } catch (_) {}
      }
      if (isPressed) {
        isPressed = false;
        btn.classList.remove('active');
        onStateChange(false);
      }
    };

    btn.addEventListener('pointerdown', press, { passive: false });
    btn.addEventListener('pointerup', release, { passive: false });
    btn.addEventListener('pointercancel', release, { passive: false });
    btn.addEventListener('pointerleave', (e) => {
      if (!btn.hasPointerCapture || !btn.hasPointerCapture(e.pointerId)) {
        release(e);
      }
    }, { passive: false });
    btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); });
    btn.addEventListener('dblclick', (e) => { e.preventDefault(); e.stopPropagation(); });
    btn.addEventListener('contextmenu', (e) => { e.preventDefault(); e.stopPropagation(); });

    return btn;
  }

  setInteractHighlight(active, label = 'INTERACT') {
    if (!this.interactBtn) return;
    const subLabel = this.interactBtn.querySelector('.nintendo-btn-sub');
    if (subLabel) {
      const clean = label.replace(/\[.*?\]/g, '').trim().toUpperCase() || 'INTERACT';
      subLabel.innerText = clean;
    }

    if (active) {
      this.interactBtn.classList.add('highlight');
    } else {
      this.interactBtn.classList.remove('highlight');
    }
  }

  show(visible = true) {
    if (!this.container) return;
    if (visible) {
      this.container.classList.remove('hidden');
    } else {
      this.container.classList.add('hidden');
    }
  }

  destroy() {
    window.removeEventListener('resize', this.boundUpdateLayout);
    window.removeEventListener('orientationchange', this.boundUpdateLayout);
    window.removeEventListener('mobile-layout-refresh', this.boundUpdateLayout);

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    this.leftWing = null;
    this.rightWing = null;
    this.interactBtn = null;
  }
}
